/**
 * Protected Route Component
 *
 * Wraps routes that require authentication.
 * Redirects to login if user is not authenticated.
 * Optionally checks for required role (e.g., Admin).
 */

import React, { useContext } from 'react';
import { Route, Redirect } from 'react-router-dom';
import { UserLoginContext } from '../../context/UserLoginContext';
import { isAuthenticated } from '../../services/authService';

const ProtectedRoute = ({ component: Component, requiredRole, ...rest }) => {
  const [userState] = useContext(UserLoginContext);

  return (
    <Route
      {...rest}
      render={(props) => {
        // Check if user is authenticated
        if (!isAuthenticated() || !userState.status) {
          // Redirect to login if not authenticated
          // Preserve the intended destination in state so we can redirect back after login
          return (
            <Redirect
              to={{
                pathname: '/auth/login',
                state: { from: props.location }
              }}
            />
          );
        }

        // Check if route requires a specific role
        if (requiredRole) {
          // Support both single role (string) and multiple roles (array)
          const allowedRoles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
          if (!allowedRoles.includes(userState.role)) {
            // User is authenticated but doesn't have required role
            // Redirect to dashboard
            return <Redirect to="/admin/dashboard" />;
          }
        }

        // User is authenticated and has required role (if any)
        // Render the component
        return <Component {...props} />;
      }}
    />
  );
};

export default ProtectedRoute;
